import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Sun, Moon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const Header = () => {
  const { navigationData, contactData, language, toggleLanguage, theme, toggleTheme } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    if (window.location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header className={`header-nav ${isScrolled ? 'scrolled' : ''}`}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center"
        >
          <a 
            href="#hero" 
            onClick={(e) => { e.preventDefault(); handleNavClick('#hero'); }}
            className="text-lg font-semibold tracking-tight"
            style={{ color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}
          >
            {navigationData.logo}
          </a>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navigationData.links.map((link, index) => (
            <motion.a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="body-small hover:opacity-70 transition-opacity duration-200"
              style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
            >
              {link.label}
            </motion.a>
          ))}
        </nav>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-4"
        >
          <a
            href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de agendar.`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary hidden sm:inline-flex"
          >
            {navigationData.cta}
          </a>

          <button
            onClick={toggleTheme}
            className="flex items-center gap-1 p-2 rounded-full hover:bg-black/5 transition-colors"
            title={theme === 'dark' ? (language === 'en' ? 'Light mode' : 'Modo Claro') : (language === 'en' ? 'Dark mode' : 'Modo Escuro')}
            style={{ color: 'var(--text-primary)' }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1 p-2 rounded-full hover:bg-black/5 transition-colors"
            title={language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
            style={{ color: 'var(--text-primary)' }}
          >
            <Globe size={18} />
            <span className="text-sm font-medium uppercase">{language}</span>
          </button>
          
          {/* Mobile Menu Button */}
          <button
            className="btn-nav lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </motion.div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-20 z-50 lg:hidden"
            style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(12px)' }}
          >
            <nav className="flex flex-col p-6 gap-4">
              {navigationData.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className="body-medium py-2 border-b"
                  style={{ 
                    color: 'var(--text-primary)', 
                    textDecoration: 'none',
                    borderColor: 'var(--border-light)'
                  }}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de agendar.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-4 text-center block"
              >
                {navigationData.cta}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
